import { useCallback, useMemo, useState } from "react";
import {
    DelimitedArrayParam,
    StringParam,
    useQueryParams,
} from "use-query-params";
import { ScheduleTemplateListItem } from "@common/schemas/domains/accounting/scheduleTemplate.schema";
import { useStateAwareNavigate } from "@hooks/useStateAwareNavigate";
import { NewScheduleGroupModal } from "./NewScheduleGroupModal";
import { ScheduleApplicationsTab } from "./applications/ScheduleApplicationsTab";
import { ScheduleApplyModal } from "./ScheduleApplyModal";
import { createScheduleApplyNavigateUrl } from "./scheduleApplyNavigate";
import { ScheduleDrawer } from "./ScheduleDrawer";
import {
    SCHEDULES_HUB_TABS,
    SchedulesHubTab,
    SchedulesHubTabs,
    isSchedulesHubTab,
} from "./SchedulesHubTabs";
import { SchedulesList } from "./SchedulesList";
import {
    ScheduleTemplateLifecycleModals,
    ScheduleTemplateLifecycleRequest,
} from "./ScheduleTemplateLifecycleModals";
import { useScheduleHubRedirect } from "./useScheduleHubRedirect";
import styles from "./SchedulesPage.module.scss";

function resolveActiveTab(queryParams: {
    tab: string | null | undefined;
    templateId: string | null | undefined;
}): SchedulesHubTab {
    if (isSchedulesHubTab(queryParams.tab)) {
        return queryParams.tab;
    }
    return queryParams.templateId
        ? SCHEDULES_HUB_TABS.schedules
        : SCHEDULES_HUB_TABS.policies;
}

export const SchedulesPage: React.FC = () => {
    const navigate = useStateAwareNavigate();
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [duplicateSourceId, setDuplicateSourceId] = useState<string | null>(
        null,
    );
    const [initialGroup, setInitialGroup] = useState<string | undefined>();
    const [isNewGroupOpen, setIsNewGroupOpen] = useState(false);
    const [applyingTemplate, setApplyingTemplate] =
        useState<ScheduleTemplateListItem | null>(null);
    const [templateLifecycleRequest, setTemplateLifecycleRequest] =
        useState<ScheduleTemplateLifecycleRequest | null>(null);
    // `tab` picks the hub tab; `templateId` scopes the Schedules tab to one
    // policy (set by a policy's journal-entry count and by the Schedules
    // report deep link, which predates `tab` and so implies the Schedules tab).
    const [queryParams, setQueryParams] = useQueryParams({
        tab: StringParam,
        templateId: StringParam,
        applicationId: StringParam,
        applicationIds: DelimitedArrayParam,
        counterparty: StringParam,
        periodStart: StringParam,
        periodEnd: StringParam,
    });
    useScheduleHubRedirect(queryParams.templateId ?? null);
    const activeTab = resolveActiveTab(queryParams);
    const setActiveTab = useCallback(
        (tab: SchedulesHubTab) =>
            setQueryParams(
                {
                    tab,
                    templateId: undefined,
                    applicationId: undefined,
                    applicationIds: undefined,
                    counterparty: undefined,
                    periodStart: undefined,
                    periodEnd: undefined,
                },
                "replaceIn",
            ),
        [setQueryParams],
    );
    const showTemplateSchedules = useCallback(
        (template: ScheduleTemplateListItem) =>
            setQueryParams({
                tab: SCHEDULES_HUB_TABS.schedules,
                templateId: template.id,
                applicationId: undefined,
                applicationIds: undefined,
            }),
        [setQueryParams],
    );
    const showAppliedSchedule = useCallback(
        (applicationId: string) =>
            setQueryParams({
                tab: SCHEDULES_HUB_TABS.schedules,
                templateId: undefined,
                applicationId,
                applicationIds: undefined,
                counterparty: undefined,
                periodStart: undefined,
                periodEnd: undefined,
            }),
        [setQueryParams],
    );
    const setScopedApplicationId = useCallback(
        (applicationId: string | null) =>
            setQueryParams(
                { applicationId: applicationId ?? undefined },
                "replaceIn",
            ),
        [setQueryParams],
    );
    const clearScopedApplicationIds = useCallback(
        () => setQueryParams({ applicationIds: undefined }, "replaceIn"),
        [setQueryParams],
    );
    const scopedApplicationIds = (queryParams.applicationIds ?? []).flatMap(
        (id) => (id ? [id] : []),
    );
    const setScopedCounterpartyName = useCallback(
        (counterpartyName: string | null) =>
            setQueryParams(
                { counterparty: counterpartyName ?? undefined },
                "replaceIn",
            ),
        [setQueryParams],
    );
    const setScopedPeriod = useCallback(
        ({ start, end }: { start: string | null; end: string | null }) =>
            setQueryParams(
                {
                    periodStart: start ?? undefined,
                    periodEnd: end ?? undefined,
                },
                "replaceIn",
            ),
        [setQueryParams],
    );
    const scopeTemplateIdToTab = useCallback(
        (tab: SchedulesHubTab) => (templateId: string | null) =>
            setQueryParams(
                { tab, templateId: templateId ?? undefined },
                "replaceIn",
            ),
        [setQueryParams],
    );
    const setScopedPolicyId = useMemo(
        () => scopeTemplateIdToTab(SCHEDULES_HUB_TABS.policies),
        [scopeTemplateIdToTab],
    );
    const setScopedTemplateId = useMemo(
        () => scopeTemplateIdToTab(SCHEDULES_HUB_TABS.schedules),
        [scopeTemplateIdToTab],
    );
    const tabNavigation = (
        <SchedulesHubTabs activeTab={activeTab} onTabChange={setActiveTab} />
    );

    const openCreate = useCallback((group?: string) => {
        setEditingId(null);
        setDuplicateSourceId(null);
        setInitialGroup(group);
        setIsDrawerOpen(true);
    }, []);

    const openEdit = useCallback((template: ScheduleTemplateListItem) => {
        setEditingId(template.id);
        setDuplicateSourceId(null);
        setInitialGroup(undefined);
        setIsDrawerOpen(true);
    }, []);

    const openDuplicate = useCallback((template: ScheduleTemplateListItem) => {
        setEditingId(null);
        setDuplicateSourceId(template.id);
        setInitialGroup(undefined);
        setIsDrawerOpen(true);
    }, []);

    const closeDrawer = useCallback(() => {
        setIsDrawerOpen(false);
        setEditingId(null);
        setDuplicateSourceId(null);
        setInitialGroup(undefined);
    }, []);

    return (
        <div className={styles.page}>
            {activeTab === SCHEDULES_HUB_TABS.schedules ? (
                <ScheduleApplicationsTab
                    templateId={queryParams.templateId ?? null}
                    onTemplateIdChange={setScopedTemplateId}
                    applicationId={queryParams.applicationId ?? null}
                    onApplicationIdChange={setScopedApplicationId}
                    applicationIds={scopedApplicationIds}
                    onApplicationIdsClear={clearScopedApplicationIds}
                    counterpartyName={queryParams.counterparty ?? null}
                    onCounterpartyNameChange={setScopedCounterpartyName}
                    periodStart={queryParams.periodStart ?? null}
                    periodEnd={queryParams.periodEnd ?? null}
                    onPeriodChange={setScopedPeriod}
                    tabNavigationTop={tabNavigation}
                />
            ) : (
                <SchedulesList
                    tabNavigationTop={tabNavigation}
                    templateId={queryParams.templateId ?? null}
                    onTemplateIdChange={setScopedPolicyId}
                    onCreate={() => openCreate()}
                    onNewGroup={() => setIsNewGroupOpen(true)}
                    onApply={setApplyingTemplate}
                    onEdit={openEdit}
                    onViewApplied={showTemplateSchedules}
                    onDuplicate={openDuplicate}
                    onArchive={(template) =>
                        setTemplateLifecycleRequest({
                            action: "archive",
                            template,
                        })
                    }
                    onRestore={(template) =>
                        setTemplateLifecycleRequest({
                            action: "restore",
                            template,
                        })
                    }
                    onDelete={(template) =>
                        setTemplateLifecycleRequest({
                            action: "delete",
                            template,
                        })
                    }
                />
            )}
            <ScheduleTemplateLifecycleModals
                request={templateLifecycleRequest}
                onClose={() => setTemplateLifecycleRequest(null)}
                onRequestChange={setTemplateLifecycleRequest}
            />
            <ScheduleDrawer
                open={isDrawerOpen}
                templateId={editingId}
                duplicateSourceId={duplicateSourceId}
                initialGroup={initialGroup}
                onClose={closeDrawer}
            />
            <NewScheduleGroupModal
                show={isNewGroupOpen}
                onHide={() => setIsNewGroupOpen(false)}
                onCreate={(group) => openCreate(group)}
            />
            <ScheduleApplyModal
                show={applyingTemplate != null}
                template={applyingTemplate}
                onHide={() => setApplyingTemplate(null)}
                onApplied={showAppliedSchedule}
                onNavigate={(destination) => {
                    if (!applyingTemplate) {
                        return;
                    }
                    navigate(
                        createScheduleApplyNavigateUrl({
                            destination,
                            entityIds: applyingTemplate.entityIds,
                            scheduleTemplateId: applyingTemplate.id,
                        }),
                    );
                }}
            />
        </div>
    );
};
